import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';

/**
 * Shared confirm dialog, rebuilt on MUI 6 + the cardiac theme (navy/teal).
 *
 * Same contract as the previous SCSS version — props, callbacks and text are unchanged, so
 * every existing caller (delete report, save report, status changes, GP account actions…)
 * keeps working. `isPatientReport` is still accepted because callers pass it; it only ever
 * drove a stylesheet hook, so it no longer affects rendering.
 *
 * `destructive` opts into the red confirm button for irreversible actions.
 */

interface ConfirmationModalProps {
  title?: string;
  description?: string;
  isConfirmationModalOpen?: boolean;
  handleCloseModal?: () => void;
  handleConfirm?: () => void;
  cancelActionText?: string;
  confirmActionText?: string;
  isPatientReport?: boolean;
  destructive?: boolean;
}

const ConfirmationModal = ({
  title,
  description,
  isConfirmationModalOpen,
  handleCloseModal,
  handleConfirm,
  cancelActionText,
  confirmActionText,
  destructive = false,
}: ConfirmationModalProps) => {
  if (!isConfirmationModalOpen) {
    return null;
  }

  return (
    <Dialog
      open
      onClose={handleCloseModal}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(2, 8, 15, 0.72)', backdropFilter: 'blur(3px)' },
        },
        paper: {
          sx: theme => ({
            borderRadius: 3,
            border: `1px solid ${theme.palette.cardiac.line}`,
            backgroundImage: `linear-gradient(180deg, ${theme.palette.cardiac.surface2} 0%, ${theme.palette.cardiac.surface} 100%)`,
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)',
            px: 1,
            py: 0.5,
          }),
        },
      }}
    >
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, fontSize: 20, color: 'text.primary', mb: description ? 1.25 : 0 }}
        >
          {title}
        </Typography>
        {description ? (
          <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: 'text.secondary' }}>
            {description}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCloseModal}
            sx={theme => ({
              borderColor: theme.palette.cardiac.lineStrong,
              color: 'text.primary',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.1,
              '&:hover': {
                borderColor: theme.palette.cardiac.ink3,
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
              },
            })}
          >
            {cancelActionText || 'Cancel'}
          </Button>
          <Button
            fullWidth
            variant="contained"
            color={destructive ? 'error' : 'primary'}
            onClick={handleConfirm}
            sx={{ textTransform: 'none', fontWeight: 700, py: 1.1 }}
          >
            {confirmActionText || 'Confirm'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationModal;
